import { useNavigate } from "react-router-dom";
import { useGenerateRoutes } from "@router/router-helper";
import { useAppSelector } from "@store/store-helper";
import { useCallback, useMemo } from "react";
import { getMemberDisplayName } from "@utils/data-display";
import { isCompanyContext } from "@utils/type-guards";
import { useAppRouteMatch } from "@hooks/use-route-match";
import { projectStateSelector } from "@store/project/project-slice";
import { companyStateSelector } from "@store/workspace/workspace-slice";
import { memberStateSelector } from "@store/member/member-slice";
import { Breadcrumb, Breadcrumbs } from "@flat-ui";

/** Main component to create breadcrumbs based on the subject */
export function SubjectBreadcrumbs(): React.JSX.Element {
  const {
    generateHomeRoute,
    generateCompanyRoute,
    generateMemberRoute,
    generateProjectRoute,
  } = useGenerateRoutes();
  const routeIdentifier = useAppRouteMatch();
  const { company } = useAppSelector(companyStateSelector);
  const { project } = useAppSelector(projectStateSelector);
  const { member } = useAppSelector(memberStateSelector);
  const navigate = useNavigate();

  const breadCrumbData = useMemo(() => {
    const items: Breadcrumb[] = [
      { text: "Home", link: generateHomeRoute().pathname },
    ];

    if (routeIdentifier === "about-products") {
      items.push({ text: "About products" });
      return items;
    }

    if (routeIdentifier === "member" && member) {
      items.push({
        text: getMemberDisplayName(member),
        link: generateMemberRoute({ memberId: member.identity }).pathname,
      });
    }

    if (
      (routeIdentifier === "workspace" ||
        routeIdentifier === "workspace-add-plan" ||
        routeIdentifier === "workspace-add-feature" ||
        routeIdentifier === "workspace-add-device-plan" ||
        routeIdentifier === "add-project") &&
      company
    ) {
      items.push({
        text: company.name,
        link: generateCompanyRoute({ companyId: company.id }).pathname,
      });
    }

    if (routeIdentifier === "workspace-add-plan") {
      items.push({ text: "Add subscription" });
    }

    if (routeIdentifier === "workspace-add-feature") {
      items.push({ text: "Add feature" });
    }

    if (routeIdentifier === "workspace-add-device-plan") {
      items.push({ text: "Add device plan" });
    }

    if (routeIdentifier === "add-project") {
      items.push({ text: "Add project" });
    }

    if (
      (routeIdentifier === "project" ||
        routeIdentifier === "project-add-plan" ||
        routeIdentifier === "project-add-feature") &&
      project
    ) {
      if (isCompanyContext(project.context)) {
        items.push({
          text: project.context.company.name,
          link: generateCompanyRoute({ companyId: project.context.company.id })
            .pathname,
        });
      }
      items.push({
        text: project.name,
        link: generateProjectRoute({ projectId: project.id }).pathname,
      });
    }

    if (routeIdentifier === "project-add-plan") {
      items.push({ text: "Add subscription" });
    }

    if (routeIdentifier === "project-add-feature") {
      items.push({ text: "Add feature" });
    }

    return items;
  }, [
    member,
    company,
    generateMemberRoute,
    generateCompanyRoute,
    generateHomeRoute,
    generateProjectRoute,
    project,
    routeIdentifier,
  ]);

  /** Track clicks */
  const handleBreadcrumbClick = useCallback(
    (breadcrumb: Breadcrumb) => {
      if (breadcrumb.link) {
        navigate(breadcrumb.link);
      }
    },
    [navigate]
  );

  return (
    <Breadcrumbs
      aria-label="breadcrumb"
      breadcrumbs={breadCrumbData}
      onBreadcrumbClick={handleBreadcrumbClick}
    />
  );
}
